import type { BrokerBenchmarks } from "@/types";

/** The personal daily-publishing ring.
 *
 *  Producer-only by design. It used to render for every role off
 *  `agency.placement_goal_daily`, so shareholders and both manager tiers — who
 *  have published nothing, ever — met a 40-a-day target as the loudest element
 *  on their home page.
 *
 *  Sized to match a `Stat` tile's height so it lines up in the KPI row instead
 *  of setting its own taller rhythm.
 */
export function TodayCard({ b }: { b: BrokerBenchmarks }) {
  const goal = b.placement_goal_daily;
  const pct = goal > 0 ? Math.min(1, b.placements_today / goal) : 0;
  const hit = goal > 0 && b.placements_today >= goal;
  const R = 26;
  const C = 2 * Math.PI * R;

  return (
    <div className="card sm:col-span-2 px-3.5 py-3 bg-gradient-to-br from-accent-500/[0.07] via-transparent to-transparent flex items-start gap-3.5 min-w-0">
      {goal > 0 ? (
        <div className="relative flex-shrink-0 self-center">
          <svg width="66" height="66" viewBox="0 0 66 66" className="-rotate-90">
            <circle cx="33" cy="33" r={R} fill="none" strokeWidth="6" className="stroke-white/[0.07]" />
            <circle
              cx="33"
              cy="33"
              r={R}
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C * (1 - pct)}
              className={`${hit ? "stroke-emerald-400" : "stroke-accent-400"} transition-all duration-700`}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-semibold tabular-nums leading-none text-slate-100">
              {b.placements_today}
            </span>
            <span className="text-[9px] text-slate-500 tabular-nums">of {goal}</span>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 w-[66px] text-center self-center">
          <div className="text-3xl font-semibold tabular-nums text-slate-100 leading-none">
            {b.placements_today}
          </div>
        </div>
      )}

      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Published today
        </div>
        <div className="mt-1 text-[13px] font-medium text-slate-200 truncate">
          {hit
            ? "Daily goal hit — nice."
            : goal > 0
              ? `${goal - b.placements_today} more to hit today's goal`
              : "Listings published today"}
        </div>
        {goal > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span
              className={`badge text-[10px] ${
                hit
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 bg-white/[0.03] text-slate-400"
              }`}
            >
              {Math.round(pct * 100)}% of goal
            </span>
            <span className="badge text-[10px] border-white/10 bg-white/[0.03] text-slate-400">
              {b.placement_days_hit}/{b.placement_days_elapsed} days on target
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
